import type { Camera } from '../core/footage';
import { positionAt, type PlacedClip, type Timeline } from '../core/timeline';
import { VirtualClock } from './clock';
import type { Tile } from './grid';
import type { LibraryFile } from './library';
import { correctDrift, isBlocking, isPlaybackRefused, isStarting, StallGuard } from './sync';

const STILL_TOLERANCE_S = 0.02;
const PRELOAD_AHEAD_MS = 4000;
const CAMERA_FRAME_MS = 1000 / 36;

type Placed = PlacedClip<LibraryFile>;
type Wait = 'none' | 'start' | 'stall';

interface Player {
  readonly tile: Tile;
  loaded: Placed | undefined;
  url: string | undefined;
  pendingSeek: number | undefined;
  playFrom: number | undefined;
}

export interface FrontFrame {
  readonly placed: Placed;
  readonly mediaTime: number;
}

export class Playback {
  readonly clock: VirtualClock;
  private readonly players: readonly Player[];
  private animation = 0;
  private scrubbing = false;
  private readonly stall = new StallGuard();
  private frontFrame: FrontFrame | undefined;

  constructor(
    private readonly timeline: Timeline<LibraryFile>,
    tiles: readonly Tile[],
    private readonly isVisible: (tile: Tile) => boolean,
    private readonly onTick: (time: number) => void,
  ) {
    this.clock = new VirtualClock(timeline.start, timeline.end);
    this.players = tiles.map((tile) => ({ tile, loaded: undefined, url: undefined, pendingSeek: undefined, playFrom: undefined }));
    this.players.forEach((player) => this.watch(player));
  }

  get front(): FrontFrame | undefined {
    return this.frontFrame;
  }

  start(time: number): void {
    this.clock.seek(time);
    this.animation = requestAnimationFrame(this.tick);
  }

  stop(): void {
    cancelAnimationFrame(this.animation);
    for (const player of this.players) {
      player.tile.video.pause();
      player.tile.video.removeAttribute('src');
      player.tile.video.load();
      if (player.url) URL.revokeObjectURL(player.url);
    }
  }

  toggle(): void {
    if (this.clock.playing) this.clock.pause();
    else this.clock.play();
    // Synced inside the tap: browsers that start video only from a user gesture refuse a play() made on the next frame.
    this.step();
  }

  seek(time: number, scrubbing = false): void {
    this.scrubbing = scrubbing;
    this.clock.seek(time);
  }

  stepFrame(direction: 1 | -1): void {
    this.clock.pause();
    this.clock.seek(this.clock.time() + direction * CAMERA_FRAME_MS);
  }

  private readonly tick = (): void => {
    this.animation = requestAnimationFrame(this.tick);
    this.step();
  };

  private step(): void {
    const time = this.clock.time();
    const positions = this.players.map((player) => ({ player, position: this.isVisible(player.tile) ? positionAt(this.timeline, player.tile.camera, time) : undefined }));
    const blocked = positions.some(({ player, position }) => position !== undefined && this.isBlocked(player, position.placed, position.mediaTime));
    const starting = positions.some(({ player, position }) => position !== undefined && this.stillStarting(player));
    const waiting = this.stall.waiting(this.clock.playing && (blocked || starting));
    this.clock.setWaiting(waiting);
    const wait: Wait = waiting ? (blocked ? 'stall' : 'start') : 'none';
    for (const { player, position } of positions) this.sync(player, time, position && { placed: position.placed, mediaTime: position.mediaTime }, wait);
    this.onTick(time);
  }

  private stillStarting(player: Player): boolean {
    const { video } = player.tile;
    return isStarting({ playFrom: player.playFrom, currentTime: video.currentTime, paused: video.paused, ended: video.ended, failed: video.error !== null });
  }

  private isBlocked(player: Player, placed: Placed, mediaTime: number): boolean {
    const { video } = player.tile;
    player.tile.element.toggleAttribute('data-error', video.error !== null);
    return isBlocking({
      loaded: player.loaded === placed,
      readyState: video.readyState,
      seeking: video.seeking,
      ended: video.ended,
      failed: video.error !== null,
      mediaTime,
      span: placed.span,
    });
  }

  private sync(player: Player, time: number, position: FrontFrame | undefined, wait: Wait): void {
    const visible = this.isVisible(player.tile);
    player.tile.element.toggleAttribute('data-gap', visible && position === undefined);
    if (!visible) {
      player.tile.video.pause();
    } else if (!position) {
      this.idleInGap(player, time);
    } else {
      if (player.loaded !== position.placed) this.load(player, position.placed, position.mediaTime);
      if (!this.clock.playing || wait === 'stall') this.hold(player, position.mediaTime);
      else if (wait === 'start') this.play(player);
      else this.follow(player, position.mediaTime);
    }
  }

  private follow(player: Player, mediaTime: number): void {
    const { video } = player.tile;
    const correction = correctDrift(mediaTime, video.currentTime, this.clock.rate);
    if (correction.kind === 'seek') this.requestSeek(player, correction.time);
    else video.playbackRate = correction.rate;
    this.play(player);
  }

  private play(player: Player): void {
    const { video } = player.tile;
    if (!video.paused || video.seeking) return;
    player.playFrom = video.currentTime;
    video.play().catch((error: unknown) => {
      if (isPlaybackRefused(error)) this.clock.pause();
    });
  }

  private hold(player: Player, mediaTime: number): void {
    const { video } = player.tile;
    if (!video.paused) video.pause();
    if (Math.abs(video.currentTime - mediaTime) > STILL_TOLERANCE_S) this.requestSeek(player, mediaTime);
  }

  private idleInGap(player: Player, time: number): void {
    player.tile.video.pause();
    const upcoming = this.timeline.slots
      .flatMap((slot) => slot.clips)
      .find((placed) => placed.clip.camera === player.tile.camera && placed.start > time && placed.start - time <= PRELOAD_AHEAD_MS);
    if (upcoming && player.loaded !== upcoming) this.load(player, upcoming, 0);
  }

  private load(player: Player, placed: Placed, mediaTime: number): void {
    const previous = player.url;
    player.url = URL.createObjectURL(placed.clip.ref.file);
    player.loaded = placed;
    player.pendingSeek = mediaTime;
    player.tile.video.src = player.url;
    // iOS WebKit caps preload at metadata; only an explicit load() builds the player a seek needs to finish.
    player.tile.video.load();
    if (previous) URL.revokeObjectURL(previous);
  }

  private requestSeek(player: Player, time: number): void {
    const { video } = player.tile;
    if (video.seeking || video.readyState < HTMLMediaElement.HAVE_METADATA) {
      player.pendingSeek = time;
      return;
    }
    player.pendingSeek = undefined;
    if (!video.paused) player.playFrom = time;
    if (this.scrubbing && typeof video.fastSeek === 'function') video.fastSeek(time);
    else video.currentTime = time;
  }

  private watch(player: Player): void {
    const { video } = player.tile;
    const flush = (): void => {
      const pending = player.pendingSeek;
      if (pending !== undefined) this.requestSeek(player, pending);
    };
    video.addEventListener('loadedmetadata', flush);
    video.addEventListener('seeked', flush);
    if (player.tile.camera !== 'front' || !('requestVideoFrameCallback' in video)) return;
    const onFrame = (_now: number, metadata: VideoFrameCallbackMetadata): void => {
      if (player.loaded) this.frontFrame = { placed: player.loaded, mediaTime: metadata.mediaTime };
      video.requestVideoFrameCallback(onFrame);
    };
    video.requestVideoFrameCallback(onFrame);
  }

  frontAt(time: number): FrontFrame | undefined {
    const position = positionAt(this.timeline, 'front' satisfies Camera, time);
    return position && { placed: position.placed, mediaTime: position.mediaTime };
  }
}
